import { NgClass } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { downloadReportTypeOptions } from 'dashboard/constants/downloadReportTypesOptions';
import { ReportTypes } from 'dashboard/enums/report-type.enum';
import { DashboardWidgetConfigurationInterface } from 'dashboard/interfaces/dashboard-widget-configuration.interface';
import { downloadReportsListInterface } from 'dashboard/interfaces/download-reports.interface';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { EMPTY, catchError, map } from 'rxjs';
import { SELECTED_LANGUAGE_STORAGE_KEY, supportedLanguages } from 'shared/constants/languages';
import { NotificationStatusEnum } from 'shared/constants/notification-statuses';
import { EventBusService, StorageService, ToastService } from 'shared/services';
import { NotificationsService } from 'shared/services/notifications.service';

@Component({
    selector: 'app-download-reports-dashboard-widget',
    templateUrl: './download-reports-dashboard-widget.component.html',
    styleUrls: ['./download-reports-dashboard-widget.component.scss'],
    imports: [TranslateModule, NgClass],
})
export class DownloadReportsDashboardWidgetComponent implements OnInit {
    @Input() dashboardWidgetConfiguration?: DashboardWidgetConfigurationInterface;

    protected reports: downloadReportsListInterface[] = [];

    constructor(
        private dashboardService: DashboardService,
        private translate: TranslateService,
        private storage: StorageService,
        private toastService: ToastService,
        private notificationsService: NotificationsService,
        private destroyRef: DestroyRef,
        private eventBusService: EventBusService,
    ) {}

    ngOnInit(): void {
        this.dashboardService
            .getAvailableDownloadReportTypes()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((availableReportTypes) => {
                this.reports = availableReportTypes.map((reportType) => this.getDownloadReportTypeOption(reportType));
                this.setIsInProgressFlag();
            });

        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setIsInProgressFlag());
    }

    public downloadReport(reportType: ReportTypes): void {
        const currentReport = this.reports.find((report) => report.reportType === reportType)!;
        currentReport.isInProgress = true;

        const currentLanguageCode =
            (this.storage.getFromLocal(SELECTED_LANGUAGE_STORAGE_KEY) as string) ?? supportedLanguages[0].languageCode;
        const currentLanguageName = supportedLanguages.find((lang) => lang.languageCode === currentLanguageCode)?.label;

        // Make a request to listingApi/settings/reports
        this.dashboardService
            .downloadReport(currentLanguageCode, reportType)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    if (error.status === 400) {
                        this.toastService.showToast(
                            this.translate.instant('dashboard.languageNotAvailableForSupplier', { languageName: currentLanguageName }),
                            'error',
                        );
                        return EMPTY;
                    }

                    if (error.status === 404) {
                        this.toastService.showToast(this.translate.instant('dashboard.noLiveListingForSupplier'), 'error');
                        return EMPTY;
                    }

                    if (error.status === 502) {
                        this.toastService.showToast(this.translate.instant('dashboard.somethingWentWrong'), 'error');
                        return EMPTY;
                    }

                    throw error;
                }),
            )
            .subscribe(() => {
                this.toastService.showToast(this.translate.instant('dashboard.reportIsInProcessingMode'), 'success');
                return EMPTY;
            });
    }

    private setIsInProgressFlag() {
        this.reports.forEach((report) => {
            this.notificationsService
                .getOperationStatus(report.operationName, 1, 1)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    map((response: any) => response.data?.[0]?.status),
                )
                .subscribe((status: any) => {
                    report.isInProgress = status == NotificationStatusEnum.IN_PROGRESS;
                });
        });
    }

    private getDownloadReportTypeOption(reportTypeValue: ReportTypes) {
        const index = downloadReportTypeOptions.findIndex((option) => option.reportType === reportTypeValue);
        return downloadReportTypeOptions[index];
    }
}
